/**
 * EPG (Event Planning Grid) Type Definitions
 *
 * TypeScript interfaces for EPG API requests and responses.
 * Mirrors backend Pydantic models in backend/models/epg.py.
 *
 * Created: 2025-12-14
 * Updated: 2025-12-15 - Aligned with backend Month Code enum (JAN, FEB, MAR)
 * Phase: 2 - Frontend Build
 */

/**
 * Month code enum - matches backend MonthCode enum exactly
 * NOTE: Uses 3-letter codes (JAN, FEB, MAR) NOT YYYY-MM format
 */
export enum MonthCode {
  JAN = 'JAN',
  FEB = 'FEB',
  MAR = 'MAR',
  APR = 'APR',
  MAY = 'MAY',
  JUN = 'JUN',
  JUL = 'JUL',
  AUG = 'AUG',
  SEP = 'SEP',
  OCT = 'OCT',
  NOV = 'NOV',
  DEC = 'DEC',
}

/**
 * Owner information
 */
export interface OwnerInfo {
  id: number;
  name: string;
}

/**
 * Organization summary model for list view
 * Matches backend OrganizationSummary model
 */
export interface EPGOrganization {
  id: number;
  name: string;
  is_15dp_opportunity?: boolean; // Always true when filtered
  last_activity_date?: string | null; // ISO timestamp
  owner?: OwnerInfo | null;
  address?: string | null; // Organization address
  event_count?: number; // Count of assigned events across all categories
  // Allow dynamic Pipedrive field hash keys (40-character strings)
  // These are used by extractEventsFromOrganization() to populate grid cells
  [fieldHash: string]: unknown;
}

/**
 * Event option model - matches backend EventOption
 */
export interface EventOption {
  id: number;
  label: string;
  name?: string; // Backend compatibility alias for label
  is_separator: boolean;
  color: string | null;
  category_type: string | null; // "education", "social", etc.
  category?: string;
  month?: MonthCode;
}

/**
 * Contact information - matches backend ContactInfo (basic fields only)
 */
export interface ContactInfo {
  id?: number; // Some EPG contact endpoints return id instead of person_id
  person_id?: number;
  name: string;
  title?: string | null;
  email_work?: string | null;
  work_location?: string | null;
  organization?: string;
  organization_id?: number | null;
  organization_contact_priority?: number | null;
  event_types?: string | null;
}

/**
 * Column identifiers for EPG contacts table
 * Matches desktop EPG contact_panel.py COLUMNS structure
 */
export enum EPGContactColumn {
  // Selection (column 0)
  SELECT = '_select',

  // Priority (column 1)
  ORG_PRIORITY = 'org_priority',

  // Event Type Checkboxes (columns 2-8)
  EVENT_TYPE_EDUCATION = 'event_type_education',
  EVENT_TYPE_SPORTING = 'event_type_sporting',
  EVENT_TYPE_CONCERTS = 'event_type_concerts',
  EVENT_TYPE_TASTINGS = 'event_type_tastings_hh',
  EVENT_TYPE_OUTDOORS = 'event_type_outdoors',
  EVENT_TYPE_USI = 'event_type_usi',
  EVENT_TYPE_WEBINAR = 'event_type_webinar',

  // Data Columns (columns 8-25)
  LAST_ACTIVITY_DATE = 'last_activity_date',
  ACTIVITY_TRIGGER = 'activity_trigger',
  NEXT_ACTIVITY_DESCRIPTION = 'next_activity_description',
  NAME = 'name',
  TITLE = 'title',
  ORGANIZATION = 'organization',
  WORK_LOCATION = 'work_location',
  EMAIL_WORK = 'email_work',
  EMAIL_HOME = 'email_home',
  EMAIL_OTHER = 'email_other',
  LAST_INVITATION_SENT = 'last_invitation_sent',
  LAST_INVITE_DETAILS = 'last_invite_details',
  PERSON_CREATED = 'person_created',
  TOTAL_ACTIVITIES = 'total_activities',
  ACTIVITY_OVERVIEW = 'activity_overview',
  ACTIVITIES_TO_DO = 'activities_to_do',
  EMAIL_MESSAGES_COUNT = 'email_messages_count',
  PERSON_ID = 'person_id',
}

/**
 * Role category for contact filtering
 * Used to classify contacts by their organizational role
 */
export type RoleCategory = 'CXO' | 'Finance' | 'HR' | null;

/**
 * Full EPG contact interface with all 27 columns from desktop application
 * Matches contact_panel.py COLUMNS structure (1 select + 1 priority + 7 event types + 18 data fields)
 */
export interface EPGContact {
  // Core identification
  person_id: number;
  name: string;
  organization: string;
  organization_id?: number | null;

  // Organization contact priority (1-5, displayed as "1st", "2nd", etc.)
  organization_contact_priority?: number | null;

  // Role category for filtering (CXO, Finance, HR, or null for unclassified)
  role_category?: RoleCategory;

  // Event Types (7 checkboxes) - stored as comma-separated string in event_types field
  event_types?: string; // Comma-separated: "Education, Sporting, Concerts, etc."

  // Contact Information
  title: string | null;
  email_work: string | null;
  email_home: string | null;
  email_other: string | null;
  work_location: string | null;

  // Activity Tracking
  last_activity_date: string | null; // ISO timestamp or date string
  activity_trigger: string | null; // Dropdown: triggers for activity
  next_activity_description: string | null; // Dropdown: description of next activity
  total_activities: number | null;
  activity_overview: string | null;
  activities_to_do: number | null;

  // Email/Communication
  email_messages_count: number | null;
  last_invitation_sent: string | null; // ISO timestamp or date string
  last_invite_details: string | null;

  // Metadata
  person_created: string | null; // ISO timestamp or date string

  // UI-specific fields (for rendering color bands in contact table)
  company_color_index?: number; // 0 = Blue band, 1 = Green band (alternates per organization)

  // Custom Pipedrive fields (dynamic)
  [key: string]: unknown;
}

/**
 * Contact selection state for checkbox tracking
 * Used by contact panel for bulk operations
 */
export interface ContactSelection {
  person_id: number;
  selected: boolean;
  has_pending_changes: boolean;
}

/**
 * Contact grouping by organization (company bands)
 * Used for alternating blue/green row colors in contact table
 */
export interface ContactGrouping {
  organization_name: string;
  contacts: EPGContact[];
  color_index: number; // 0 = Blue, 1 = Green
  start_row: number;
  end_row: number;
}

/**
 * Events by month - matches backend EventsByMonth
 */
export interface EventsByMonth {
  field_key: string; // Pipedrive field hash
  selected_ids: number[];
  selected_labels: string[];
  events_selected_labels?: string[];
  calls_emails_selected_labels?: string[];
  drops_selected_labels?: string[];
  selected_event_ids?: number[]; // Legacy alias for selected_ids
  available_events?: EPGEvent[];
  month_name?: string;
  // Category-specific fields (for desktop EPG parity)
  events_selected_ids?: number[];
  calls_emails_selected_ids?: number[];
  drops_selected_ids?: number[];
  // Completion tracking (Phase: Event Completion Feature)
  completed_event_ids?: number[]; // IDs of completed events this month
}

/**
 * Event completion data - tracks when events are marked as completed
 * Used for tracking email invites and other campaign activities
 */
export interface EventCompletion {
  id: number;
  org_id: number;
  event_id: number;
  field_key: string;
  month: string; // Format: "2026-01" (YYYY-MM)
  completed_at: string; // ISO timestamp
  completed_by?: string; // User identifier
  campaign_type: string; // e.g., 'email_invite', 'phone_call', 'drop'
  metadata?: Record<string, unknown>; // Additional context data
}

// Alias for compatibility
export type EPGEventsByMonth = Record<string, EventsByMonth>;

/**
 * EPG Event type for frontend usage
 */
export interface EPGEvent {
  id: number;
  label: string;
  category?: string;
  color?: string | null;
  description?: string;
  name?: string; // Alias for label
  marketing_category?: string; // Category name
  event_type?: string; // Event type (Events, Drops, Calls/Emails)
  date?: string; // Event date
}

/**
 * Month data for calendar grid display
 * Contains available events and selection state for a single month
 */
export interface EPGMonthData {
  available_events: EPGEvent[];
  selected_event_ids: number[];
  completed_event_ids?: number[]; // IDs of events marked as completed
}

/**
 * Organization detail with contacts and marketing data
 * Matches backend OrganizationDetail model
 */
export interface OrganizationDetail {
  id: number;
  name: string;
  contacts: ContactInfo[];
  marketing_data: Record<string, number[]>; // e.g., "JAN_Events": [909, 911]
}

/**
 * ============================================
 * API Request/Response Types
 * ============================================
 */

/**
 * GET /api/epg/organizations - Paginated organization list
 * Matches backend OrganizationsListResponse
 */
export interface OrganizationsListResponse {
  organizations: EPGOrganization[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  last_sync: string | null; // ISO timestamp
}

/**
 * GET /api/epg/organizations/:id/events - Organization events
 * Matches backend EventsResponse
 */
export interface EventsResponse {
  org_id: number;
  events_by_month: Record<string, EventsByMonth>; // MonthCode -> EventsByMonth
}

/**
 * PUT /api/epg/organizations/:id/events - Update events for a month
 * Matches backend UpdateEventsRequest
 */
export interface UpdateEventsRequest {
  month: MonthCode;
  event_ids: number[]; // Max 15 events per month
  sync_to_pipedrive?: boolean;
}

/**
 * PUT /api/epg/organizations/:id/events - Update events response
 * Matches backend UpdateEventsResponse
 */
export interface UpdateEventsResponse {
  org_id: number;
  month: MonthCode;
  updated_event_ids: number[];
  synced_to_pipedrive: boolean;
  sync_timestamp: string | null; // ISO timestamp
}

/**
 * POST /api/epg/batch-update - Batch update request
 * Matches backend BatchUpdateRequest
 */
export interface BatchUpdateItem {
  org_id: number;
  month: MonthCode;
  event_ids: number[];
  category: string; // Required by backend: 'Events', 'Calls & Emails', or 'Drops'
}

export interface BatchUpdateRequest {
  updates: BatchUpdateItem[];
  sync_to_pipedrive?: boolean;
}

/**
 * POST /api/epg/batch-update - Batch update response
 * Matches backend EPGBatchUpdateResponse exactly
 */
export interface BatchUpdateResponse {
  success: boolean;
  successful: number;
  failed: number;
  errors: string[];
}

/**
 * GET /api/epg/event-options/:category - Event options by category
 * Matches backend EventOptionsResponse
 */
export interface EventOptionsResponse {
  category: string; // "Events", "Calls & Emails", "Drops"
  months: Record<string, EventOption[]>; // MonthCode -> EventOption[]
  data?: EventOption[]; // Legacy flat response from /epg/events/available
  events_by_category?: Record<string, EPGEvent[]>; // Category -> Event[]
  categories?: string[]; // List of categories
}

export interface CellEdit {
  orgId: number;
  category: string;
  month: MonthCode;
  oldIds: number[];
  newIds: number[];
}

/**
 * Event options organized by category with month-based structure
 * Phase 1 - Event Palette Data Structure
 */
export interface CategoryEventOptions {
  months: Record<MonthCode, EventOption[]>;
  isLoading: boolean;
  lastLoaded: Date | null;
}

/**
 * Event options by category map
 * Phase 1 - Event Palette Data Structure
 */
export type EventOptionsByCategory = Record<string, CategoryEventOptions>;

/**
 * GET /api/epg/sync/status - Sync status
 * Matches backend SyncStatusResponse
 */
export interface SyncStatusResponse {
  last_sync_timestamp: string | null; // ISO timestamp
  pending_org_changes: number;
  pending_contact_changes: number;
  can_sync: boolean;
  has_conflicts: boolean;
}

/**
 * POST /api/epg/sync/commit - Commit sync request
 * Matches backend SyncCommitRequest
 */
export interface SyncCommitRequest {
  include_orgs?: boolean;
  include_contacts?: boolean;
}

/**
 * POST /api/epg/sync/commit - Commit sync response
 * Matches backend SyncCommitResponse
 */
export interface SyncCommitResponse {
  total_changes: number;
  org_changes_synced: number;
  contact_changes_synced: number;
  failed_count: number;
  sync_timestamp: string; // ISO timestamp
}

/**
 * ============================================
 * Frontend-Specific Types (Not in Backend)
 * ============================================
 */

/**
 * Organization list query parameters
 */
export interface OrganizationListParams {
  page?: number; // Default: 1
  limit?: number; // Default: 50, Max: 100
  search?: string | null;
}

/**
 * Event filter row for multi-row filtering
 * Each row specifies a Month + Event Type combination
 */
export interface EventFilterRow {
  id: string; // Unique ID for React keys (nanoid)
  month: string; // 'Any Month' | 'JAN' | 'FEB' | ... | 'DEC'
  eventTypeId: number | null; // Event option ID from fieldDefinitions
  eventTypeLabel: string | null; // Display label for the event type
  inverse?: boolean; // If true, filter for organizations WITHOUT this event (default: false = includes)
}

/**
 * Comparison operators for numeric/date field filtering
 */
export type FieldFilterOperator =
  | 'eq'      // equals
  | 'ne'      // not equals
  | 'lt'      // less than
  | 'lte'     // less than or equal
  | 'gt'      // greater than
  | 'gte'     // greater than or equal
  | 'between' // between two values
  | 'empty'   // is empty/null
  | 'notEmpty'; // is not empty/null

/**
 * Field filter row for numeric/date/enum organization field filtering
 * Allows filtering organizations by field values with comparison operators
 */
export interface FieldFilterRow {
  id: string; // Unique ID for React keys (nanoid)
  fieldKey: string; // Pipedrive field key/hash
  fieldLabel: string; // Display label (e.g., "15DP Lives (5500)")
  fieldType: 'int' | 'double' | 'date' | 'enum'; // Field data type
  operator: FieldFilterOperator;
  value: string | number | null; // Primary value
  value2?: string | number | null; // Second value for 'between' operator
  enumOptions?: { id: number; label: string }[]; // Options for enum fields
}

/**
 * Predefined field filter definitions for EPG
 * Maps user-friendly names to Pipedrive field hashes
 */
export const EPG_FIELD_FILTER_DEFINITIONS: Array<{
  key: string;
  label: string;
  type: 'int' | 'double' | 'date' | 'enum';
  options?: { id: number; label: string }[];
}> = [
  {
    key: '59684158576527ad43fb692fc70b98ed17d0f5a8',
    label: '15DP Lives (5500)',
    type: 'int',
  },
  {
    key: '58b58794f1d7312ae510ed16af6392bf43208289',
    label: 'Total Commissions',
    type: 'double',
  },
  {
    key: '2b1a4b8898568867b58590c28ec6bd4425f0658e',
    label: '4A Premium',
    type: 'double',
  },
  {
    key: 'last_activity_date',
    label: 'Last Activity',
    type: 'date',
  },
  {
    key: '243827718ad17df5018f1beb33ad90672bec1998',
    label: 'Priority',
    type: 'enum',
    options: [
      { id: 1856, label: 'Hot' },
      { id: 1857, label: 'Medium' },
      { id: 1858, label: 'Low' },
    ],
  },
  {
    key: '033857b465f111f37d06b0653be055f81feecfe1',
    label: 'Size',
    type: 'enum',
    options: [
      { id: 169, label: 'Large' },
      { id: 170, label: 'Medium' },
      { id: 171, label: 'Small' },
    ],
  },
  {
    key: '63cc01618286977721db93935dd8bc3b1c76c478',
    label: 'Effective Date',
    type: 'date',
  },
  {
    key: '71430edb73341a0a13bda97d1470d9699b41f6c2',
    label: '15DP_5500 (Y/N)',
    type: 'enum',
    options: [
      { id: 21, label: 'Yes' },
      { id: 22, label: 'No' },
      { id: 105, label: 'Fully Insured' },
      { id: 106, label: 'Self Insured' },
    ],
  },
];

/**
 * Campaign filter for EPG
 */
export interface CampaignFilter {
  campaignId: string;
  campaignName: string;
  /** Organization IDs in this campaign (fetched from API) */
  organizationIds?: number[];
}

/**
 * EPG filter state
 */
export interface EPGFilters {
  search: string;
  sortBy: 'name' | 'last_activity_date';
  sortOrder: 'asc' | 'desc';
  quickFilters: {
    hasEvents: boolean;
    recentActivity: boolean;
  };
  // Simple filters (Phase 4)
  category?: string | null; // 'All', 'Events', 'Calls & Emails', 'Drops'
  month?: string | null; // 'Any Month', 'JAN', 'FEB', etc.
  eventOption?: number | null; // Specific event option ID
  inverseFilter?: boolean; // Show WITHOUT selected event/month
  // Advanced filters (Phase 4)
  advancedFilters?: AdvancedFilterRow[];
  savedFilterName?: string | null; // Currently loaded saved filter
  // Multi-row event filters (toolbar filter button)
  eventFilterRows?: EventFilterRow[]; // Multiple month+event filter rows
  eventFilterLogic?: 'AND' | 'OR'; // How multiple rows combine
  // Organization field filters (numeric/date/enum)
  fieldFilterRows?: FieldFilterRow[]; // Multiple field filter rows
  fieldFilterLogic?: 'AND' | 'OR'; // How multiple field filter rows combine
  // Campaign filter - filters organizations to those in the selected campaign
  campaignFilter?: CampaignFilter | null;
}

/**
 * Advanced filter row configuration
 */
export interface AdvancedFilterRow {
  source: 'organization' | 'contact';
  fieldKey: string; // Pipedrive field key/hash
  fieldName: string; // Display name
  fieldType: string; // varchar, int, date, enum, etc.
  condition: string; // equals, contains, between, etc.
  value: string; // Filter value
}

/**
 * Saved filter preset
 */
export interface SavedFilterPreset {
  name: string;
  filters: EPGFilters;
  createdAt: string; // ISO timestamp
  updatedAt: string; // ISO timestamp
}

/**
 * EPG view preferences (localStorage)
 */
export interface EPGViewPreferences {
  compactMode: boolean;
  showEventDescriptions: boolean;
  defaultYear: number;
  organizationListWidth: number;
  zoomLevel: number; // 0.5, 0.75, 1.0, 1.25, 1.5
  zoom?: number; // Alias for zoomLevel (25, 50, 75, 100, 125 - percentage format)
  visibleMonthCount: number; // 3, 4, 6, 9, 12 (Phase 4 - P4-T07)
  monthsVisible: number; // 3, 4, 6, 9, 12 (alias for visibleMonthCount)
  startMonth: number; // 0-11 (for scrolling when showing <12 months) (Phase 4 - P4-T08)
  monthScrollOffset: number; // 0-9 (for scrolling when showing <12 months)
  rowVisibility: {
    Events: boolean;
    Calls: boolean;
    Drops: boolean;
  };
  highlightEmptyMonths: boolean; // Phase 4 - P4-T09
  contactsPanelLayout: 'side' | 'grid'; // 'side' = left panel (default), 'grid' = 4-column grid below table
  eventsPanelPosition: 'left' | 'right'; // 'left' = events panel on left, 'right' = events panel on right (default)
  /**
   * Ordered array of field_key hashes for the EPG metadata row.
   * Users can select up to 10 fields from the unified field catalog.
   * These replace the legacy hard-coded fields (Created, Owner, Events, Address, Completed).
   */
  customFieldKeys: string[];
}

/**
 * Unsaved changes tracking
 */
export interface EPGUnsavedChanges {
  org_id: number;
  org_name: string;
  updates: BatchUpdateItem[];
  timestamp: string; // ISO timestamp
}

/**
 * Pending change for cell-level tracking (Phase 6)
 * Stored in backend for persistence across sessions
 */
export interface PendingChange {
  orgId: number;
  orgName: string; // Required by backend API
  category: string;
  month: MonthCode;
  oldIds: number[];
  newIds: number[];
}

/**
 * Default preferences
 */
export const DEFAULT_EPG_PREFERENCES: EPGViewPreferences = {
  compactMode: false,
  showEventDescriptions: true,
  defaultYear: new Date().getFullYear(),
  organizationListWidth: 320,
  zoomLevel: 1.0,
  zoom: 100, // Percentage format (25, 50, 75, 100, 125) - used by ZoomControls and TransposedGrid
  visibleMonthCount: 4, // P2-T1: Changed default from 12 to 4 months
  monthsVisible: 4, // P2-T1: Changed default from 12 to 4 months
  startMonth: 0, // Default to January (user requested: always start on January events)
  monthScrollOffset: 0,
  rowVisibility: {
    Events: true,
    Calls: true,
    Drops: true,
  },
  highlightEmptyMonths: false,
  contactsPanelLayout: 'side', // Default: show contacts in side panel
  eventsPanelPosition: 'right', // Default: show events panel on right side
  customFieldKeys: [], // Default: empty (shows empty state until user configures)
};

/**
 * ============================================
 * Utility Functions
 * ============================================
 */

/**
 * Convert month number (1-12) to MonthCode enum
 */
export function monthNumberToCode(month: number): MonthCode {
  const codes = [
    MonthCode.JAN,
    MonthCode.FEB,
    MonthCode.MAR,
    MonthCode.APR,
    MonthCode.MAY,
    MonthCode.JUN,
    MonthCode.JUL,
    MonthCode.AUG,
    MonthCode.SEP,
    MonthCode.OCT,
    MonthCode.NOV,
    MonthCode.DEC,
  ];
  if (month < 1 || month > 12) {
    throw new Error(`Invalid month number: ${month}. Must be 1-12.`);
  }
  return codes[month - 1];
}

/**
 * Convert MonthCode enum to month number (1-12)
 */
export function monthCodeToNumber(code: MonthCode): number {
  const map: Record<MonthCode, number> = {
    [MonthCode.JAN]: 1,
    [MonthCode.FEB]: 2,
    [MonthCode.MAR]: 3,
    [MonthCode.APR]: 4,
    [MonthCode.MAY]: 5,
    [MonthCode.JUN]: 6,
    [MonthCode.JUL]: 7,
    [MonthCode.AUG]: 8,
    [MonthCode.SEP]: 9,
    [MonthCode.OCT]: 10,
    [MonthCode.NOV]: 11,
    [MonthCode.DEC]: 12,
  };
  return map[code];
}

/**
 * Get month name from MonthCode
 */
export function getMonthName(code: MonthCode, year?: number): string {
  const monthNum = monthCodeToNumber(code);
  const date = new Date(year || new Date().getFullYear(), monthNum - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: year ? 'numeric' : undefined });
}

/**
 * Generate all 12 month codes
 */
export function getAllMonthCodes(): MonthCode[] {
  return [
    MonthCode.JAN,
    MonthCode.FEB,
    MonthCode.MAR,
    MonthCode.APR,
    MonthCode.MAY,
    MonthCode.JUN,
    MonthCode.JUL,
    MonthCode.AUG,
    MonthCode.SEP,
    MonthCode.OCT,
    MonthCode.NOV,
    MonthCode.DEC,
  ];
}

/**
 * Get MonthCode for current month
 */
export function getCurrentMonthCode(): MonthCode {
  const now = new Date();
  return monthNumberToCode(now.getMonth() + 1);
}

/**
 * Parse month key string to extract month number
 * Handles formats like "2025-01", "JAN", or month codes
 */
export function parseMonthKey(key: string): { month: number; year?: number } {
  // Handle YYYY-MM format
  if (key.includes('-')) {
    const [year, month] = key.split('-').map(Number);
    return { month, year };
  }

  // Handle MonthCode format (JAN, FEB, etc.)
  if (key in MonthCode) {
    return { month: monthCodeToNumber(key as MonthCode) };
  }

  // Handle numeric month
  const num = parseInt(key);
  if (!isNaN(num) && num >= 1 && num <= 12) {
    return { month: num };
  }

  throw new Error(`Invalid month key format: ${key}`);
}

/**
 * Generate year-month keys for a given year
 * Returns array of strings in format "YYYY-MM" for all 12 months
 */
export function generateYearMonthKeys(year: number): string[] {
  return Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    return `${year}-${month.toString().padStart(2, '0')}`;
  });
}

/**
 * EPG category names
 */
export const EPG_CATEGORIES = ['Events', 'Calls & Emails', 'Drops'] as const;

/**
 * EPG category colors constant
 */
export const EPG_CATEGORY_COLORS: Record<string, string> = {
  'Events': '#3B82F6', // Blue
  'Calls & Emails': '#10B981', // Green
  'Drops': '#F59E0B', // Amber
  'Meetings': '#8B5CF6', // Purple
  'Tasks': '#EC4899', // Pink
  'Social': '#EF4444', // Red
  'Education': '#06B6D4', // Cyan
};

/**
 * Get category color based on category name
 * Returns a consistent color for each category type
 */
export function getCategoryColor(category: string): string {
  return EPG_CATEGORY_COLORS[category] || '#6B7280'; // Default gray
}

/**
 * Validate event IDs array (max 15 events per month)
 */
export function validateEventIds(eventIds: number[]): { valid: boolean; error?: string } {
  if (eventIds.length > 15) {
    return {
      valid: false,
      error: `Maximum 15 events per month. You selected ${eventIds.length} events.`,
    };
  }
  return { valid: true };
}

/**
 * Type guards
 */
export function isEPGOrganization(obj: unknown): obj is EPGOrganization {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof (obj as Record<string, unknown>).id === 'number' &&
    typeof (obj as Record<string, unknown>).name === 'string' &&
    typeof (obj as Record<string, unknown>).is_15dp_opportunity === 'boolean'
  );
}

export function isEventOption(obj: unknown): obj is EventOption {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof (obj as Record<string, unknown>).id === 'number' &&
    typeof (obj as Record<string, unknown>).label === 'string' &&
    typeof (obj as Record<string, unknown>).is_separator === 'boolean'
  );
}

export function isEPGContact(obj: unknown): obj is EPGContact {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof (obj as Record<string, unknown>).person_id === 'number' &&
    typeof (obj as Record<string, unknown>).name === 'string' &&
    typeof (obj as Record<string, unknown>).organization === 'string'
  );
}

/**
 * Contact priority display values
 */
export const CONTACT_PRIORITY_OPTIONS = ['', '1st', '2nd', '3rd', '4th', '5th'] as const;

/**
 * Event type labels (7 types from Pipedrive)
 */
export const EVENT_TYPE_LABELS = [
  'Education',
  'Sporting',
  'Concerts',
  'Tastings & HH',
  'Outdoors',
  'USI EB Events',
  'Webinar',
] as const;

/**
 * Convert contact priority number (1-5) to display format ("1st", "2nd", etc.)
 */
export function contactPriorityToDisplay(priority: number | null | undefined): string {
  if (!priority || priority < 1 || priority > 5) return '';
  return CONTACT_PRIORITY_OPTIONS[priority];
}

/**
 * Convert contact priority display format ("1st", "2nd") to number (1-5)
 */
export function contactPriorityToNumber(display: string): number | null {
  const index = CONTACT_PRIORITY_OPTIONS.indexOf(display as typeof CONTACT_PRIORITY_OPTIONS[number]);
  return index > 0 ? index : null;
}

/**
 * Parse event types string into array
 */
export function parseEventTypes(eventTypesStr: string | null | undefined): string[] {
  if (!eventTypesStr) return [];
  return eventTypesStr.split(',').map((t) => t.trim()).filter(Boolean);
}

/**
 * Check if contact has specific event type
 */
export function contactHasEventType(contact: EPGContact, eventType: string): boolean {
  const types = parseEventTypes(contact.event_types);
  return types.includes(eventType);
}

/**
 * Company color palette for alternating row backgrounds
 */
export const COMPANY_COLORS = {
  blue: {
    light: '#232d3c', // RGB(35, 45, 60)
    dark: '#192332',  // RGB(25, 35, 50)
  },
  green: {
    light: '#23372d', // RGB(35, 55, 45)
    dark: '#192d23',  // RGB(25, 45, 35)
  },
} as const;

/**
 * ============================================
 * Field Definitions Types (Pipedrive Custom Fields Parity)
 * ============================================
 */

/**
 * Single option for a set/enum field from Pipedrive
 */
export interface FieldDefinitionOption {
  id: number;
  label: string;
}

/**
 * Single Pipedrive field definition
 */
export interface FieldDefinition {
  name: string;
  field_type: string; // 'set', 'enum', 'varchar', 'int', etc.
  options: FieldDefinitionOption[];
  last_updated: string | null; // ISO timestamp
}

/**
 * GET /api/epg/field-definitions - Response
 */
export interface FieldDefinitionsResponse {
  success: boolean;
  fields: Record<string, FieldDefinition>; // field_key -> definition
  categories: Record<string, string[]>; // category -> field_keys[]
  last_sync: string | null; // ISO timestamp
}

/**
 * POST /api/epg/field-definitions/sync - Response
 */
export interface SyncFieldDefinitionsResponse {
  success: boolean;
  fields_updated: number;
  last_sync: string;
  errors: string[];
}
